<template>
  <f7-page name="gasto-ia" class="gia-page">
    <f7-navbar title="Gasto de IA" :back-link="$t('atras')"></f7-navbar>

    <div class="gia">
      <div class="hero">
        <h1>Gasto de IA en la ERP</h1>
        <!-- #815 · La cifra se rotula como ESTIMADA: sale de los tokens que devuelve cada proveedor
             por su tarifa publicada, no de su factura. Quien compare con la factura tiene que saberlo
             antes de leer el número, no después. -->
        <div class="sub">
          <b>Coste estimado</b>: tokens consumidos × tarifa del modelo en el momento de cada llamada.
          No es la factura del proveedor.
        </div>
      </div>

      <div class="periodo">
        <label for="gia-periodo">Periodo</label>
        <select id="gia-periodo" v-model="periodo" @change="alCambiarPeriodo">
          <option v-for="p in periodos" :key="p.id" :value="p.id">{{ p.etiqueta }}</option>
        </select>
        <template v-if="periodo === 'otro'">
          <label for="gia-desde">Desde</label>
          <input id="gia-desde" type="date" v-model="desdeOtro" />
          <label for="gia-hasta">Hasta</label>
          <input id="gia-hasta" type="date" v-model="hastaOtro" />
          <button class="giabtn" :disabled="!!motivoOtro" @click="consultarOtro">Consultar</button>
          <!-- §9: un «Consultar» deshabilitado dice por qué, junto al botón. -->
          <span class="motivo" v-if="motivoOtro">{{ motivoOtro }}</span>
        </template>
        <span class="rango" v-if="rangoConsultado">{{ rotuloRango(rangoConsultado) }}</span>
      </div>

      <!-- Tres estados que no se confunden: cargando, no se ha podido leer y esto es lo que hay. Si
           el registro no se ha podido leer no se pinta ningún total: un «0,00 €» diría que no se ha
           gastado nada, que es justo lo que no se sabe. -->
      <div class="estado" v-if="cargando">
        <f7-preloader></f7-preloader><span>Leyendo el registro de gasto de IA…</span>
      </div>
      <div class="fallo" v-else-if="error">
        <div class="t">⚠️ {{ error.titulo }}</div>
        <div class="m">{{ error.mensaje }}</div>
        <div class="m" v-if="error.quien">{{ error.quien }}</div>
        <button class="giabtn" v-if="error.reintentable" @click="cargar">Reintentar</button>
      </div>
      <template v-else-if="resumen">
        <div class="kpis">
          <div class="kpi total">
            <div class="lab">Gasto estimado del periodo</div>
            <div class="val">{{ eur(resumen.total.costeEur) }}</div>
            <div class="ksub">≈ {{ usd(resumen.total.costeUsd) }} · en dólares, que es como facturan los proveedores</div>
          </div>
          <div class="kpi">
            <div class="lab">Llamadas a IA</div>
            <div class="val">{{ entero(resumen.total.llamadas) }}</div>
            <div class="ksub" v-if="resumen.total.llamadasError">{{ entero(resumen.total.llamadasError) }} con error</div>
            <div class="ksub" v-else>ninguna con error</div>
          </div>
          <div class="kpi">
            <div class="lab">Tokens</div>
            <div class="val">{{ entero(resumen.total.tokens.total) }}</div>
            <div class="ksub">{{ entero(entradaDe(resumen.total.tokens)) }} de entrada · {{ entero(resumen.total.tokens.salida) }} de salida</div>
          </div>
        </div>

        <!-- Lo que la cifra no cuenta, JUNTO al total: si no, quien la compare con la factura ve un
             descuadre y no sabe de dónde sale. -->
        <ul class="avisos" v-if="avisos.length">
          <li v-for="(a, i) in avisos" :key="i">{{ a }}</li>
        </ul>

        <div class="card">
          <h3>Gasto por servicio</h3>
          <div class="cs">De más a menos gasto. El porcentaje es sobre el gasto estimado del periodo.</div>
          <div class="tblwrap">
            <table class="tbl">
              <thead><tr>
                <th>Servicio</th><th class="tr">Llamadas</th><th class="tr">Tokens</th>
                <th class="tr">Coste estimado</th><th class="tr">% del total</th>
              </tr></thead>
              <tbody>
                <tr v-for="s in resumen.servicios" :key="s.servicio">
                  <td>
                    <div class="nombre">{{ s.nombre }}</div>
                    <div class="alias">{{ s.servicio }}</div>
                  </td>
                  <td class="tr">
                    {{ entero(s.llamadas) }}
                    <div class="nota" v-if="s.llamadasError">{{ entero(s.llamadasError) }} con error</div>
                    <div class="nota" v-if="s.llamadasSinCoste">{{ entero(s.llamadasSinCoste) }} sin coste calculable</div>
                  </td>
                  <td class="tr">{{ entero(s.tokens.total) }}</td>
                  <td class="tr b">{{ eur(s.costeEur) }}</td>
                  <td class="tr">{{ porcentaje(s.porcentaje) }}</td>
                </tr>
                <tr v-if="!resumen.servicios.length">
                  <td colspan="5" class="vacio">No hay ninguna llamada a IA registrada en este periodo.</td>
                </tr>
              </tbody>
              <tfoot v-if="resumen.servicios.length">
                <tr>
                  <td>Total</td>
                  <td class="tr">{{ entero(resumen.total.llamadas) }}</td>
                  <td class="tr">{{ entero(resumen.total.tokens.total) }}</td>
                  <td class="tr">{{ eur(resumen.total.costeEur) }}</td>
                  <td class="tr">{{ resumen.total.costeEur > 0 ? '100 %' : '—' }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="pie" v-if="resumen.servicios.length > 1">
            Cada importe se redondea al céntimo; el total es la suma exacta del desglose, así que la suma
            de los importes redondeados puede diferir en algún céntimo.
          </div>
        </div>

        <div class="row2" v-if="resumen.servicios.length">
          <div class="card">
            <h3>Por proveedor</h3>
            <div class="cs">Para compararlo con la factura de cada proveedor, que viene en dólares.</div>
            <table class="tbl">
              <thead><tr><th>Proveedor</th><th class="tr">Llamadas</th><th class="tr">Estimado €</th><th class="tr">Estimado $</th></tr></thead>
              <tbody>
                <tr v-for="p in proveedores" :key="p.proveedor">
                  <td>{{ p.nombre }}</td>
                  <td class="tr">{{ entero(p.llamadas) }}</td>
                  <td class="tr b">{{ eur(p.costeEur) }}</td>
                  <td class="tr">{{ usd(p.costeUsd) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="card">
            <h3>Por modelo</h3>
            <div class="cs">El modelo que respondió en cada llamada.</div>
            <table class="tbl">
              <thead><tr>
                <th>Modelo</th><th class="tr">Llamadas</th><th class="tr">Entrada</th><th class="tr">Salida</th><th class="tr">Estimado €</th>
              </tr></thead>
              <tbody>
                <tr v-for="m in resumen.modelos" :key="m.proveedor + '|' + m.modelo">
                  <td>
                    <div class="nombre">{{ m.modelo || 'Sin modelo' }}</div>
                    <div class="alias">{{ nombreProveedor(m.proveedor) }}</div>
                  </td>
                  <td class="tr">{{ entero(m.llamadas) }}</td>
                  <td class="tr">{{ entero(entradaDe(m.tokens)) }}</td>
                  <td class="tr">{{ entero(m.tokens.salida) }}</td>
                  <td class="tr b">{{ eur(m.costeEur) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </f7-page>
</template>

<script>
import GastoIa from '../../js/db/GastoIa.js';
import GastoIaVista from '../../js/GastoIaVista.js';
import cerrojoDeLaSesion from '../../js/utils/cerrojoDeLaSesion.js';

export default {
  data() {
    return {
      periodos: GastoIaVista.PERIODOS,
      periodo: GastoIaVista.PERIODO_POR_DEFECTO,
      desdeOtro: '',
      hastaOtro: '',
      rangoConsultado: null,
      cargando: false,
      error: null,
      resumen: null,
      // Cada consulta lleva su número y solo pinta la última: si se cambia de periodo antes de que
      // conteste la anterior, sus cifras no pueden acabar debajo del rótulo del periodo nuevo.
      consulta: 0
    };
  },
  computed: {
    motivoOtro() {
      return GastoIaVista.motivoPeriodoInvalido(this.desdeOtro, this.hastaOtro);
    },
    avisos() {
      return GastoIaVista.avisosDe(this.resumen);
    },
    proveedores() {
      return GastoIaVista.porProveedor(this.resumen ? this.resumen.modelos : []);
    }
  },
  mounted() {
    this.rangoConsultado = GastoIaVista.rangoDe(this.periodo);
    this.cargar();
  },
  methods: {
    eur: GastoIaVista.eur,
    usd: GastoIaVista.usd,
    entero: GastoIaVista.entero,
    porcentaje: GastoIaVista.porcentaje,
    nombreProveedor: GastoIaVista.nombreProveedor,
    // Lo cacheado se cobra a otra tarifa y el servidor lo separa; para leer, es entrada igual.
    entradaDe(tokens) {
      if (!tokens) return 0;
      return (tokens.entrada || 0) + (tokens.cacheLectura || 0) + (tokens.cacheEscritura || 0);
    },
    alCambiarPeriodo() {
      if (this.periodo === 'otro') {
        // El periodo a mano parte del que se estaba viendo, para no empezar de cero.
        if (this.rangoConsultado) {
          this.desdeOtro = this.desdeOtro || this.rangoConsultado.desde;
          this.hastaOtro = this.hastaOtro || this.rangoConsultado.hasta;
        }
        return;
      }
      this.rangoConsultado = GastoIaVista.rangoDe(this.periodo);
      this.cargar();
    },
    consultarOtro() {
      if (this.motivoOtro) return;
      this.rangoConsultado = { desde: this.desdeOtro, hasta: this.hastaOtro };
      this.cargar();
    },
    cargar() {
      const self = this;
      const rango = self.rangoConsultado;
      if (!rango) return;
      const numero = ++self.consulta;
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla la continuación que lo soltaba.
      cerrojoDeLaSesion(self, 'cargando');
      self.error = null;
      self.resumen = null;
      GastoIa.resumen(rango.desde, rango.hasta).then((r) => {
        if (numero !== self.consulta) return;
        if (!r || !r.total || !Array.isArray(r.servicios)) {
          self.error = {
            titulo: 'No se ha podido leer el gasto de IA',
            mensaje: 'El servicio de IA ha contestado algo que no es un resumen de gasto.',
            quien: 'No se enseña ninguna cifra porque no se ha podido contar nada. Si sigue pasando, avisa a soporte.',
            reintentable: true
          };
          return;
        }
        self.resumen = r;
      }).catch((e) => {
        if (numero !== self.consulta) return;
        self.error = self.errorDe(e);
      }).then(() => {
        if (numero === self.consulta) self.cargando = false;
      });
    },
    errorDe(e) {
      const status = e && e.status;
      if (status === 403) {
        // §9: por qué, quién lo desbloquea y dónde. Con la entrada de menú solo para ADMIN aquí no
        // se llega sin serlo, pero si el menú de alguien estuviera mal, que no se quede en blanco.
        return {
          titulo: 'Esta pantalla es solo para administradores',
          mensaje: 'El gasto de IA solo lo pueden consultar los usuarios con perfil Administrador.',
          quien: 'Si necesitas verlo, pide a un administrador del sistema que te asigne ese perfil en la pantalla de Usuarios.',
          reintentable: false
        };
      }
      return {
        titulo: 'No se ha podido leer el gasto de IA',
        mensaje: (e && (e.message || e.error)) || 'Error desconocido al pedir el resumen de gasto.',
        quien: 'No se enseña ninguna cifra porque no se ha podido contar nada. Vuelve a intentarlo y, si sigue sin cargar, avisa a soporte.',
        reintentable: true
      };
    },
    rotuloRango(rango) {
      const fmt = new Intl.DateTimeFormat('es-ES', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' });
      const aFecha = t => new Date(t + 'T00:00:00Z');
      if (rango.desde === rango.hasta) return 'El ' + fmt.format(aFecha(rango.desde));
      return 'Del ' + fmt.format(aFecha(rango.desde)) + ' al ' + fmt.format(aFecha(rango.hasta)) + ', los dos incluidos';
    }
  }
};
</script>

<style scoped>
.gia { max-width: 1200px; margin: 0 auto; padding: 16px; }
.hero h1 { margin: 0 0 4px; font-size: 22px; color: #23355b; }
.hero .sub { color: #555; font-size: 13px; }
.periodo { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 16px 0; }
.periodo label { font-size: 13px; color: #555; }
.periodo select, .periodo input { border: 1px solid #ccd; border-radius: 4px; padding: 4px 6px; background: #fff; }
.periodo .rango { color: #23355b; font-size: 13px; margin-left: 8px; }
.periodo .motivo { color: #b45309; font-size: 12px; }
.giabtn { border: 1px solid #23355b; background: #23355b; color: #fff; border-radius: 4px; padding: 4px 12px; cursor: pointer; }
.giabtn:disabled { opacity: .45; cursor: not-allowed; }
.estado { display: flex; align-items: center; gap: 8px; padding: 16px 0; color: #555; }
.fallo { border: 1px solid #f3c2c2; background: #fff5f5; border-radius: 6px; padding: 12px 16px; margin: 12px 0; }
.fallo .t { font-weight: 600; color: #a61b1b; margin-bottom: 4px; }
.fallo .m { color: #444; font-size: 13px; margin-bottom: 6px; }
.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-bottom: 12px; }
.kpi { border: 1px solid #e3e6ee; border-radius: 6px; padding: 12px 16px; background: #fff; }
.kpi.total { border-color: #23355b; }
.kpi .lab { font-size: 12px; color: #666; text-transform: uppercase; letter-spacing: .03em; }
.kpi .val { font-size: 26px; font-weight: 700; color: #23355b; margin: 4px 0; }
.kpi .ksub { font-size: 12px; color: #666; }
.avisos { margin: 0 0 12px; padding: 10px 16px 10px 32px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: 13px; color: #6b4e00; }
.avisos li + li { margin-top: 4px; }
.card { border: 1px solid #e3e6ee; border-radius: 6px; padding: 12px 16px; background: #fff; margin-bottom: 12px; }
.card h3 { margin: 0 0 2px; font-size: 15px; color: #23355b; }
.card .cs { font-size: 12px; color: #777; margin-bottom: 8px; }
.row2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(420px, 1fr)); gap: 12px; }
.tblwrap { overflow-x: auto; }
.tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
.tbl th { text-align: left; font-weight: 600; color: #555; border-bottom: 1px solid #e3e6ee; padding: 6px 8px; }
.tbl td { border-bottom: 1px solid #f0f1f5; padding: 6px 8px; vertical-align: top; }
.tbl tfoot td { font-weight: 700; border-top: 2px solid #e3e6ee; border-bottom: none; }
.tbl .tr { text-align: right; white-space: nowrap; }
.tbl .b { font-weight: 600; }
.tbl .nombre { color: #222; }
.tbl .alias { color: #999; font-size: 11px; font-family: monospace; }
.tbl .nota { color: #b45309; font-size: 11px; }
.tbl .vacio { text-align: center; color: #777; padding: 16px; }
.pie { font-size: 11px; color: #888; margin-top: 6px; }
</style>
